import {useContext, useEffect, useRef, useState} from 'react';
import mapboxgl, {Map} from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapViewContext } from '../contexts/MapViewContext';
import nycPolygons from '../data/nyc-polygons.json';
import Legend from './Legend';
import MapPopupStyles from './MapPopupStyles';
import { createPopupContent } from '../utils/popupUtils';

mapboxgl.accessToken = 'pk.eyJ1IjoiY2FsZW5kcmUiLCJhIjoiY2lwMDk4NThhMDJpOHVmbTR6dWp3ZXIzbCJ9.QoQKozHFNWDVAiQy2c_AnQ';

const MAPBOX_USER = 'calendre';
const MAPBOX_THEME = 'cmavgem7p00c901sc1l80fwj9';

const MapboxMap: React.FC = () => {
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<Map | null>(null);

    const {
        setViewportBounds,
        sidebarCollapsed,
    } = useContext(MapViewContext);

    const [visibleTypes, setVisibleTypes] = useState<string[]>(['allowed', 'prohibited']);

    const handleToggleType = (type: string) => {
        setVisibleTypes(prev => 
            prev.includes(type) 
                ? prev.filter(t => t !== type) 
                : [...prev, type]
        );
    };

    useEffect(() => {
        if (!mapRef.current) return;
        const map = mapRef.current;

        const updateFilters = () => {
            if (!map.getLayer('nyc_polygons_fill')) return;

            const filter = ['in', ['get', 'type'], ['literal', visibleTypes]];
            map.setFilter('nyc_polygons_fill', filter);
            map.setFilter('nyc_polygons_outline', filter);
        };

        if (map.isStyleLoaded()) {
            updateFilters();
        } else {
            map.once('styledata', updateFilters);
        }
    }, [visibleTypes]);

    useEffect(() => {
        if (!mapContainerRef.current) return;

        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: `mapbox://styles/${MAPBOX_USER}/${MAPBOX_THEME}`,
            center: [-73.97, 40.77], // NYC center
            zoom: 12,
        });

        const map = mapRef.current;

        map.on('load', () => {
            // Add NYC polygons source with generateId for hover state
            map.addSource('nyc_polygons', {
                type: 'geojson',
                data: nycPolygons as any,
                generateId: true
            });

            // Add fill layer for polygons
            map.addLayer({
                id: 'nyc_polygons_fill',
                type: 'fill',
                source: 'nyc_polygons',
                layout: {},
                paint: {
                    'fill-color': [
                        'match',
                        ['get', 'type'],
                        'allowed', '#2ecc71',
                        'prohibited', '#e74c3c',
                        '#95a5a6'
                    ],
                    'fill-opacity': [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        0.7,
                        0.4
                    ]
                }
            });

            // Add outline layer for polygons
            map.addLayer({
                id: 'nyc_polygons_outline',
                type: 'line',
                source: 'nyc_polygons',
                layout: {},
                paint: {
                    'line-color': [
                        'match',
                        ['get', 'type'],
                        'allowed', '#27ae60',
                        'prohibited', '#c0392b',
                        '#7f8c8d'
                    ],
                    'line-width': [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        3,
                        1.5
                    ]
                }
            });

            // Add click handler for popups
            map.on('click', 'nyc_polygons_fill', (e) => {
                if (!e.features || e.features.length === 0) return;

                const feature = e.features[0];
                const props = feature.properties;
                if (!props) return;

                const name = props.name || 'Unknown Area';
                const type = props.type || 'Unknown';
                const description = props.description || 'No description available.';
                
                const color = type === 'allowed' ? '#2ecc71' : type === 'prohibited' ? '#e74c3c' : '#95a5a6';
                const borderColor = type === 'allowed' ? '#27ae60' : type === 'prohibited' ? '#c0392b' : '#7f8c8d';

                new mapboxgl.Popup({
                    closeButton: true,
                    closeOnClick: true,
                    maxWidth: '300px',
                    className: 'fancy-popup'
                })
                    .setLngLat(e.lngLat)
                    .setHTML(createPopupContent(name, type, description, color, borderColor))
                    .addTo(map);
            });

            let hoveredId: string | number | null = null;

            // Change cursor and highlight on hover
            map.on('mousemove', 'nyc_polygons_fill', (e) => {
                if (e.features && e.features.length > 0) {
                    map.getCanvas().style.cursor = 'pointer';
                    
                    if (hoveredId !== null) {
                        map.setFeatureState(
                            { source: 'nyc_polygons', id: hoveredId },
                            { hover: false }
                        );
                    }
                    
                    hoveredId = e.features[0].id ?? null;
                    if (hoveredId !== null) {
                        map.setFeatureState(
                            { source: 'nyc_polygons', id: hoveredId },
                            { hover: true }
                        );
                    }
                }
            });

            map.on('mouseleave', 'nyc_polygons_fill', () => {
                map.getCanvas().style.cursor = '';
                if (hoveredId !== null) {
                    map.setFeatureState(
                        { source: 'nyc_polygons', id: hoveredId },
                        { hover: false }
                    );
                }
                hoveredId = null;
            });

            setViewportBounds(map.getBounds());
        });

        map.on('moveend', () => {
            setViewportBounds(map.getBounds());
        });

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
            }
        };
    }, [setViewportBounds]);

    useEffect(() => {
        const map = mapRef.current;
        const timeout = setTimeout(() => {
            map?.resize();
        }, 10);

        return () => clearTimeout(timeout);
    }, [sidebarCollapsed]);

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            <MapPopupStyles />
            <div ref={mapContainerRef} style={{height: '100%', width: '100%'}}/>
            <Legend visibleTypes={visibleTypes} onToggleType={handleToggleType} />
        </div>
    );
};

export default MapboxMap;
